import CameraControls from "camera-controls";
import * as THREE from "three";
import { BaseService } from "./worldpanelparts/BaseService";
import { PlanetService } from "./worldpanelparts/PlanetService";
import { Skybox } from "./worldpanelparts/Skybox";
import { EventEmitter } from "events";
import { UserEventer } from "./worldpanelparts/UserEventer";
import { DataService } from "./worldpanelparts/DataService";
import { WREvents } from "./WREvents";
import { PhysXService } from "./worldpanelparts/physx/PhysXService";
import { CameraPosition } from "../visuals/visual-structs";
import { copyVec3 } from "../visuals/visual-utils";
import { GridService } from "./worldpanelparts/GridService";
import { ModelService } from "./worldpanelparts/ModelService";
import { FpvCameraService } from "./worldpanelparts/FpvCameraService";

export interface CameraRig {
  camera: THREE.Camera;
  renderer: THREE.WebGLRenderer;
}

export class WorldRenderer extends EventEmitter {
  public camera: THREE.PerspectiveCamera;
  public cameraControls!: CameraControls;
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene = new THREE.Scene();
  public sceneWidth: number;
  public sceneHeight: number;
  private clock = new THREE.Clock();
  private targetFPS = 60;
  private frameInterval = 1000 / this.targetFPS;
  public services: BaseService[];
  public dataService: DataService;
  public planetService: PlanetService;
  public physXService!: PhysXService;
  public gridService!: GridService;
  userEventer: UserEventer;
  modelService: ModelService;
  public rig: CameraRig | null = null;
  fpvCameraService: FpvCameraService;
  constructor(private div: HTMLDivElement, onReady?: (() => Promise<void>)) {
    super();
    // Clean container
    while (div.firstChild) div.removeChild(div.firstChild);
    this.sceneWidth = div.clientWidth;
    this.sceneHeight = div.clientHeight;
    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sceneWidth / this.sceneHeight,
      0.1,
      2000000000
    );
    // this.camera.name = "FPVCAMERA";
    this.camera.position.set(0, 0, 10); // Move the camera higher    
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0); // Focus on the center
    this.camera.updateProjectionMatrix(); // Update the camera matrix after changes

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(0, 1, 1);
    this.scene.add(directionalLight);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true, // Retained for smooth edges
      alpha: true, // Retained for transparent background if needed
      powerPreference: "high-performance", // Retained for optimal GPU usage
      precision: "highp", // Use highest precision for shaders
      logarithmicDepthBuffer: true, // Improve depth precision for large scenes
    });

    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // Accurate color rendering
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Soft shadows for smoother edges
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0; // Adjust exposure as needed

    this.setSize(this.sceneWidth, this.sceneHeight);
    div.appendChild(this.renderer.domElement);

    // Camera controls
    this.initCameraControls();
    this.dataService = new DataService(this);
    this.planetService = new PlanetService(this);
    this.physXService = new PhysXService(this);
    this.gridService = new GridService(this);
    this.userEventer = new UserEventer(this);
    this.modelService = new ModelService(this);
    this.fpvCameraService = new FpvCameraService(this);
    this.services = [
      this.planetService,
      this.dataService,
      this.physXService,
      this.gridService,
      // new Axis(this),
      new Skybox(this),
      this.userEventer,
      this.modelService,
      this.fpvCameraService,
    ];
    const loader = async () => {
      for (const part of this.services) {
        await part.load();
      }
      this.clock.start();
      this.startRenderingLoop();
    };
    loader().then(() => {
      if (onReady) {
        onReady().catch((error) => {
          console.error("Error in onReady callback:", error);
        });
      }
      // console.log("WorldRenderer initialized and rendering loop started.");
    }).catch((error) => {
      console.error("Error initializing WorldRenderer:", error);
    });
    // this.renderTestBox();
  }

  public getService<T extends BaseService>(partType: new (...args: any[]) => T): T {
    const part = this.services.find(p => p instanceof partType);
    if (part) {
      return part as T;
    } else {
      throw new Error(`Part of type ${partType.name} not found.`);
    }
  }

  public initCameraControls() {
    this.cameraControls = new CameraControls(
      this.camera,
      this.renderer.domElement
    );
    // this.cameraControls.maxPolarAngle = Math.PI; // so you can't look under the floor plane
    this.cameraControls.minPolarAngle = 0;
    this.cameraControls.minDistance = 1;
    this.cameraControls.maxDistance = 20000000;
    this.cameraControls.dollySpeed = 0.25;
    this.cameraControls.truckSpeed = 1.5;
    this.cameraControls.azimuthRotateSpeed = 0.15; // Adjust horizontal rotation sensitivity
    this.cameraControls.polarRotateSpeed = 0.15; // Adjust vertical rotation sensitivity
    this.cameraControls.dollyToCursor = true;
    this.cameraControls.smoothTime = 1.5;
  }

  public setCameraPosition(cp: CameraPosition) {
    if (!cp) {
      console.warn("setCameraPosition called with undefined CameraPosition");
      return;
    }
    this.camera.up.set(cp.up.x, cp.up.y, cp.up.z);
    this.cameraControls.updateCameraUp();
    this.cameraControls.setPosition(cp.position.x, cp.position.y, cp.position.z);
    this.cameraControls.setTarget(cp.target.x, cp.target.y, cp.target.z);
    this.camera.updateProjectionMatrix();
  }

  public getCameraPosition(): CameraPosition {
    const lookAt = new THREE.Vector3();
    this.cameraControls.getTarget(lookAt);
    const up = new THREE.Vector3();
    copyVec3(this.camera.up, up);
    const cp: CameraPosition = {
      position: { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z },
      target: {
        x: lookAt.x,
        y: lookAt.y,
        z: lookAt.z
      },
      up
    };
    console.log("Camera Position:", cp);
    return cp;
  }

  public setSize(width: number, height: number) {
    this.sceneWidth = width;
    this.sceneHeight = height;
    this.camera.aspect = this.sceneWidth / this.sceneHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.sceneWidth, this.sceneHeight);
  }

  private startRenderingLoop() {
    let lastFrameTime = performance.now();
    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTime < this.frameInterval) {
        requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = currentTime;
      const delta = this.clock.getDelta();
      this.cameraControls.update(delta);
      this.services.forEach(part => part.render(delta));
      this.renderer.render(this.scene, this.camera);
      if (this.rig) {
        // this.rig.camera.update();
        this.rig.renderer.render(this.scene, this.rig.camera);
      }
      requestAnimationFrame(animate);
    };
    animate(performance.now());
  }

  public dispose() {
    this.removeAllListeners();
    this.services.forEach(part => part.dispose());
    this.renderer.dispose();
    this.div.removeChild(this.renderer.domElement);
    this.cameraControls.dispose();
  }

  public emitEvent(event: WREvents, ...args: any[]) {
    this.emit(event, ...args);
  }

  public onEvent(event: WREvents, listener: (...args: any[]) => void) {
    this.addListener(event, listener);
  }

}